// Kaarten scrollen nooit zelf (oprichter 6 okt 2026: "ik wil geen inscroll in de widgets zelf, dat
// is slecht!"; docs/ux/ontwerpsysteem.md regel 13). Op Vandaag (raster) bepaalt het raster de
// hoogte van een kaart en past de inhoud zich aan, zoals data-fit op het bord: wat niet past,
// verdwijnt van achteren af in hele stukken en onderaan komt één regel "+n meer" naar de pagina
// erachter. Nooit een halve regel en nooit een scrollbalk in een kaart.
//
// Volgorde van weglaten:
//   1. regels die het minst zeggen eerst ([data-fit-early], bv. afspraken die al voorbij zijn), dan
//      de regels van een lijst (li of [data-fit-item]) van achteren af;
//   2. past het dan nog niet, losse blokken van de kaart van achteren af (nooit het eerste blok);
//   3. past zelfs dat niet, dan krijgt de kaart data-fit="clipped" (het harnas meldt dat als fout).
// Wat nooit weg mag (de bron van het weer, een waarschuwing, de gebedstijden die nog komen) staat
// binnen [data-fit="keep"]. Een regel die voor meer dingen staat (de chip "+3" van Boodschappen)
// heeft data-fit-count; een lijst die de widget zelf al inkortte heeft data-fit-extra (zoveel
// dingen staan er niet in).

const ITEMS = 'li, [data-fit-item]';
const HIDDEN = 'data-fit-hidden';

export interface FitText {
  /** "+{count} meer" */
  more: (count: number) => string;
  /** Zonder aantal ("Meer bekijken"). */
  all: string;
}

const shown = (el: Element) => el.getClientRects().length > 0;

/** Onderkant van de inhoud: de laagste onderrand van de zichtbare kinderen in de stroom. */
function contentBottom(box: HTMLElement, more: HTMLElement): number {
  let bottom = -Infinity;
  for (const child of Array.from(box.children)) {
    if (child === more || child.tagName === 'DIALOG' || !shown(child)) continue;
    const pos = getComputedStyle(child).position;
    if (pos === 'absolute' || pos === 'fixed') continue;
    bottom = Math.max(bottom, child.getBoundingClientRect().bottom);
  }
  return bottom;
}

/**
 * Laat in één kaart zien wat past. `card` is de .home-card; het eerste element in .home-card-body
 * is de kaart zelf (paneel, focuskaart, statusregel), `more` de regel "+n meer" (een broer van de
 * body, met position: absolute onderin de kaart).
 */
export function fitCard(card: HTMLElement, more: HTMLElement, text: FitText): void {
  // Eerst alles terug zoals de widget het tekende.
  for (const el of Array.from(card.querySelectorAll(`[${HIDDEN}]`))) el.removeAttribute(HIDDEN);
  more.removeAttribute('data-on');
  card.removeAttribute('data-fit');
  if (card.hidden) return;
  const body = card.querySelector<HTMLElement>('.home-card-body');
  const box = body?.firstElementChild as HTMLElement | null | undefined;
  if (!body || !box) return;

  // Fijnslijpen (7 okt 2026): een regel die op één regel moet blijven ([data-fit-line], de
  // statusregel in een kaart van één rij) laat zijn bijzin ([data-fit-drop]) helemaal weg als hij
  // niet past, in plaats van "Wie brengt Mil…".
  for (const line of Array.from(card.querySelectorAll<HTMLElement>('[data-fit-line]'))) {
    if (line.scrollWidth <= line.clientWidth + 1) continue;
    for (const drop of Array.from(line.querySelectorAll('[data-fit-drop]'))) {
      drop.setAttribute(HIDDEN, '');
    }
  }

  const boxStyle = getComputedStyle(box);
  const padBottom = parseFloat(boxStyle.paddingBottom) || 0;
  const limit = (extra: number) => {
    const b = Math.min(body.getBoundingClientRect().bottom, box.getBoundingClientRect().bottom);
    return b - padBottom - extra;
  };
  if (contentBottom(box, more) <= limit(0) + 0.5) return;

  // 0. Een regel met losse stukjes ([data-fit-row], bv. tijd, wie en labels onder de titel) blijft
  //    één regel: wat naar een tweede regel loopt, gaat helemaal weg (geen "+n meer" nodig).
  for (const row of Array.from(box.querySelectorAll<HTMLElement>('[data-fit-row]'))) {
    const kids = Array.from(row.children).filter(shown);
    if (kids.length < 2) continue;
    // Een stukje op een volgende regel begint onder het eerste stukje (ook als ze in het midden
    // van de regel staan uitgelijnd).
    const line = kids[0]!.getBoundingClientRect().bottom - 1;
    const later = kids.filter((kid) => kid.getBoundingClientRect().top >= line);
    for (const kid of later) kid.setAttribute(HIDDEN, '');
  }
  if (contentBottom(box, more) <= limit(0) + 0.5) return;

  // Er moet iets weg: de regel "+n meer" komt onderin de kaart, binnen de rand van de kaart.
  more.setAttribute('data-on', '');
  const cardRect = card.getBoundingClientRect();
  const boxRect = box.getBoundingClientRect();
  more.style.left = `${boxRect.left - cardRect.left + (parseFloat(boxStyle.paddingLeft) || 0)}px`;
  more.style.right = `${cardRect.right - boxRect.right + (parseFloat(boxStyle.paddingRight) || 0)}px`;
  const moreH = more.getBoundingClientRect().height;
  more.style.top = `${limit(moreH) - cardRect.top}px`;

  let count = 0;
  let extra = 0;
  for (const el of Array.from(box.querySelectorAll<HTMLElement>('[data-fit-extra]'))) {
    extra += Number(el.getAttribute('data-fit-extra')) || 0;
  }
  const label = () => {
    const n = count + extra;
    const value = n > 0 ? text.more(n) : text.all;
    if (more.textContent !== value) more.textContent = value;
  };
  const fits = () => contentBottom(box, more) <= limit(moreH) + 0.5;
  const hide = (el: Element, counts: boolean) => {
    el.setAttribute(HIDDEN, '');
    if (counts) count += Number(el.getAttribute('data-fit-count')) || 1;
  };

  // 1. Regels: eerst wat het minst zegt (in volgorde), dan de rest van achteren af.
  const items = Array.from(box.querySelectorAll<HTMLElement>(ITEMS)).filter(
    (el) => !el.parentElement?.closest('li') && !el.closest('[data-fit="keep"]') && shown(el),
  );
  const order = items
    .filter((el) => el.hasAttribute('data-fit-early'))
    .concat(items.filter((el) => !el.hasAttribute('data-fit-early')).reverse());
  for (const el of order) {
    hide(el, el.tagName === 'LI' || el.hasAttribute('data-fit-count'));
    if (fits()) {
      label();
      return;
    }
  }
  // 2. Losse blokken van achteren af; het eerste zichtbare blok (de kop) blijft.
  const blocks = Array.from(box.children).filter(
    (el) =>
      el !== more &&
      el.tagName !== 'DIALOG' &&
      el.getAttribute('data-fit') !== 'keep' &&
      !el.querySelector('[data-fit="keep"]') &&
      shown(el),
  );
  const first = Array.from(box.children).find((el) => shown(el));
  for (const el of blocks.reverse()) {
    if (el === first) continue;
    hide(el, false);
    if (fits()) {
      label();
      return;
    }
  }
  // 3. Past niet eens de kop: laat het zien zoals het is, maar meld het.
  label();
  card.setAttribute('data-fit', 'clipped');
}

/** Alle kaarten in het raster van Vandaag. */
export function fitCards(root: HTMLElement, text: FitText): void {
  for (const card of Array.from(root.querySelectorAll<HTMLElement>('.home-card'))) {
    const more = card.querySelector<HTMLElement>(':scope > .home-fit-more');
    if (more) fitCard(card, more, text);
  }
}
